import React, { useEffect, useRef, useMemo } from 'react';
import handyScroll from 'handy-scroll';
import { map, get } from 'lodash';
import { TTypedTFunction } from '@lib/useTypedTranslation';

import { ITableColumn, SortDirection } from './types';
import { DataTableHeader } from './dataTableHeader';
import { DataTableRow } from './dataTableRow';
import { useStickyColumns, StickyColumnsConfig } from './useStickyColumns';

import './styles.css';
import 'handy-scroll/dist/handy-scroll.css';

export interface IOnTableSort {
  (index: number, direction: SortDirection, column: ITableColumn): void
}
export interface IProps<Data, IdKey> {
  className?: string,
  columns: ITableColumn[],
  children?: any,
  selectedRow?: number,
  checkedRows?: number[],
  sortDirection?: SortDirection,
  sortedColumnIndex?: number,
  options?: {
    rowIdDataPath?: IdKey,
    getKey?: (data: Data) => string,
    rowNameDataPath?: string
  },
  data: Data[],
  onSort?: IOnTableSort,
  onRowSelected?: (rowIndex: number) => void,
  toggleSort?: boolean,
  t?: TTypedTFunction,
  onCheck?: (rowIndex: number) => void,
  onCheckAll?: () => void,
  highlight?: boolean,
  isLoading?: boolean,
  stickyColumns?: StickyColumnsConfig
}

export function DataTable<Data, IdKey extends keyof Data | void = void>(props: IProps<Data, IdKey>) {

  const columns = useMemo(() => {
    const cols = [...props.columns];
    if (props.onCheck && cols[0]) {
      cols[0] = { ...cols[0], hasCheckbox: true };
    }
    return cols;
  }, [props.columns, props.onCheck]);

  const tableEl = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = tableEl.current;
    if (!el) return;

    // Register BEFORE handyScroll.mount so this listener fires first.
    // handy-scroll's focusin handler queues setTimeout(syncWidget, 0).
    // When a sticky column's ResizeObserver fires applyStickyOffsets and
    // the resulting layout change causes a focusin (e.g. from the filter
    // reference button regaining focus after dialog close), the setTimeout
    // can fire after destroy() sets container=null, crashing syncWidget.
    // Stopping propagation here prevents handy-scroll's listener from ever
    // queuing the timer for filter-related focus events.
    const blockFilterFocusin = (e: Event) => {
      const target = e.target as Element | null;
      if (
        target?.closest('[data-id="filter-dialog-reference"]') ||
        target?.closest('[data-id="filter-dialog-popover"]') ||
        target?.closest('.list-filter')
      ) {
        e.stopImmediatePropagation();
      }
    };
    // Guard for test environments where ref may be mocked without DOM methods
    if (el.addEventListener) {
      el.addEventListener('focusin', blockFilterFocusin, false);
    }

    handyScroll.mount(el);
    return () => {
      if (el.removeEventListener) {
        el.removeEventListener('focusin', blockFilterFocusin, false);
      }
      handyScroll.destroy(el);
    };
  }, []);

  useEffect(() => {
    if (tableEl.current) {
      handyScroll.update(tableEl.current);
    }
  }, [props.data.length]);

  useStickyColumns(tableEl, props.stickyColumns, props.isLoading);

  const row = props.children || <DataTableRow />;
  const getKey = props.options?.getKey;
  const idPath = get(props, 'options.rowIdDataPath');
  const rows = map(props.data, (item, index) => {
    // NB to prevent reconciliation errors, add a getKey function, provide a rowIdDataPath for a unique value, or add `key` to data.
    // The `key` should *not* be an iteration index, or re-generated in each render.
    // getKey is a means of generating a unique key where no single property is guaranteed unique. To generate a stable, unique value, it concatonates two properties.
    // It must be a stable unique value, e.g. be a unique id generated once when the data is fetched.
    const key = getKey ? getKey(item) : get(item, idPath || 'key', index);
    return React.cloneElement(row,
      {
        key,
        idPath,
        nameDataPath: props.options?.rowNameDataPath,
        rowIndex: index,
        selectedRowIndex: props.selectedRow,
        checkedRows: props.checkedRows,
        columns,
        data: item,
        onRowSelected: props.onRowSelected,
        t: props.t,
        onCheck: props.onCheck
      });
  });

  const hasCheckAllCheckBox = Boolean(props.onCheckAll && props.data?.length);

  return (
    <div ref={tableEl} className={`${props.className || ''} data-table-component ${props.highlight ? 'highlight' : ''} ${props.isLoading ? '' : 'data-ready'} ${hasCheckAllCheckBox ? 'has-check-all-checkbox' : ''}`}>
      <table className="table table-borderless">
        <DataTableHeader
          onSort={props.onSort}
          columns={columns}
          sortedColumnIndex={props.sortedColumnIndex}
          sortDirection={props.sortDirection}
          toggleSort={props.toggleSort}
          hasCheckBox={hasCheckAllCheckBox}
          checkBoxChecked={hasCheckAllCheckBox ? props.checkedRows?.length === props.data?.length : undefined}
          checkBoxOnChange={hasCheckAllCheckBox ? props.onCheckAll : undefined}
        />
        <tbody>
          {!props.isLoading && rows}
        </tbody>
      </table>
    </div>
  );
}
